import { useCallback, useEffect, useState } from 'react';
import api, { formatApiError } from '../../lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Mail, UserPlus, RefreshCcw, Ban, CheckCircle2, Loader2, Users, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PortalUsersDialog — admin management of a customer's portal users.
 *   - List active + revoked portal users bound to this customer
 *   - List open (unaccepted) invites
 *   - Invite a new user (email + name)
 *   - Resend an invite (rolls the token, sends a fresh email)
 *   - Revoke / reactivate an existing portal user
 */
export const PortalUsersDialog = ({ open, onOpenChange, customer }) => {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteLinks, setInviteLinks] = useState({});
  const [copiedId, setCopiedId] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!customer?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customer.id}/portal-users`);
      setUsers(data.users || []);
      setInvites(data.invites || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [customer?.id]);

  useEffect(() => {
    if (open) {
      setError('');
      setInviteEmail('');
      setInviteName('');
      setInviteLinks({});
      setCopiedId('');
      load();
    }
  }, [open, load]);

  const copyLink = async (id) => {
    const link = inviteLinks[id];
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(id);
      toast.success('Invite link copied to clipboard');
      setTimeout(() => setCopiedId(''), 2000);
    } catch {
      toast.error('Could not copy — select the link and copy it manually.');
    }
  };

  // Fetch a guaranteed-valid link for ANY pending invite (even expired ones),
  // copy it to the clipboard, and reveal it inline. No email is sent.
  const copyInviteLink = async (invite) => {
    try {
      const { data } = await api.post(`/customers/${customer.id}/portal-invites/${invite.id}/link`);
      if (!data?.accept_url) { toast.error('Could not generate a link — please try Resend.'); return; }
      setInviteLinks((m) => ({ ...m, [invite.id]: data.accept_url }));
      try {
        await navigator.clipboard.writeText(data.accept_url);
        setCopiedId(invite.id);
        setTimeout(() => setCopiedId(''), 2000);
        toast.success(data.refreshed
          ? 'Link was expired — a fresh 30-day link is copied to your clipboard'
          : 'Invite link copied to clipboard');
      } catch {
        toast.message('Link ready — copy it from the field below.');
      }
      if (data.refreshed) load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setError('');
    if (!inviteEmail || !inviteName) { setError('Email and name are required.'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/customers/${customer.id}/portal-invites`,
        { email: inviteEmail.trim().toLowerCase(), name: inviteName.trim() });
      if (data?.id && data?.accept_url) {
        setInviteLinks((m) => ({ ...m, [data.id]: data.accept_url }));
      }
      toast.success(`Invite created for ${inviteEmail} — copy the link below to share it`);
      setInviteEmail('');
      setInviteName('');
      load();
    } catch (ex) {
      setError(formatApiError(ex.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const resend = async (invite) => {
    try {
      const { data } = await api.post(`/customers/${customer.id}/portal-invites/${invite.id}/resend`);
      if (data?.accept_url) {
        setInviteLinks((m) => ({ ...m, [invite.id]: data.accept_url }));
      }
      toast.success(`Fresh invite link ready for ${invite.email}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggle = async (u) => {
    const url = u.active
      ? `/customers/${customer.id}/portal-users/${u.id}/revoke`
      : `/customers/${customer.id}/portal-users/${u.id}/reactivate`;
    try {
      await api.post(url);
      toast.success(u.active ? `Revoked ${u.email}` : `Reactivated ${u.email}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl overflow-y-auto" data-testid="portal-users-dialog">
        <SheetHeader className="pb-4 border-b border-slate-100">
          <SheetTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />
            Portal users — {customer?.company_name}
          </SheetTitle>
          <SheetDescription>
            Invite this customer&apos;s contacts to their own CSG portal. They&apos;ll see only
            their own incidents and reports, read-only.
          </SheetDescription>
        </SheetHeader>

        {/* Invite form */}
        <form onSubmit={handleInvite} className="pt-5 space-y-3">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Invite a new user</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Full name</Label>
              <Input
                value={inviteName} onChange={(e) => setInviteName(e.target.value)}
                placeholder="e.g. Jane van der Merwe" className="mt-1"
                data-testid="portal-invite-name"
              />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="jane@pulsit.co.za" className="mt-1"
                data-testid="portal-invite-email"
              />
            </div>
          </div>
          {error && (
            <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
          )}
          <Button
            type="submit" disabled={busy}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="portal-invite-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1.5" /> Send invite</>}
          </Button>
        </form>

        <div className="mt-8">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Active portal users</p>
          {loading ? (
            <div className="text-sm text-slate-500 flex items-center gap-2 py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : users.length === 0 ? (
            <p className="text-sm text-slate-400 py-4" data-testid="portal-users-empty">
              No portal users yet. Send an invite above to get started.
            </p>
          ) : (
            <div className="space-y-2" data-testid="portal-users-list">
              {users.map((u, i) => {
                // Match this user back to the invite they accepted, so we can
                // show WHEN they signed up (helps admins avoid re-inviting).
                const acceptedInvite = invites.find(
                  (inv) => inv.email?.toLowerCase() === (u.email || '').toLowerCase() && inv.used_at,
                );
                return (
                  <div key={u.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-md p-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">{u.name || u.email}</p>
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                      {acceptedInvite?.used_at && (
                        <p className="text-[10px] text-emerald-600 mt-0.5 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Accepted invite on {(acceptedInvite.used_at || '').slice(0, 10)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {u.active ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500 border-slate-200">Revoked</Badge>
                      )}
                      <Button
                        variant="outline" size="sm" onClick={() => toggle(u)}
                        data-testid={`portal-user-toggle-${i}`}
                      >
                        {u.active ? <><Ban className="w-3.5 h-3.5 mr-1" /> Revoke</>
                                  : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Reactivate</>}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-8">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Pending / expired invites</p>
          {invites.filter((i) => !i.used_at).length === 0 ? (
            <p className="text-sm text-slate-400 py-4" data-testid="portal-invites-empty">No pending invites.</p>
          ) : (
            <div className="space-y-2" data-testid="portal-invites-list">
              {invites.filter((i) => !i.used_at).map((inv, i) => (
                <div key={inv.id} className="border border-slate-200 rounded-md p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-slate-400" /> {inv.email}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        Invited {(inv.created_at || '').slice(0, 10)} · expires {(inv.expires_at || '').slice(0, 10)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="default" size="sm" onClick={() => copyInviteLink(inv)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`portal-invite-copy-${i}`}
                      >
                        {copiedId === inv.id
                          ? <><Check className="w-3.5 h-3.5 mr-1" /> Copied</>
                          : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy link</>}
                      </Button>
                      <Button
                        variant="outline" size="sm" onClick={() => resend(inv)}
                        data-testid={`portal-invite-resend-${i}`}
                      >
                        <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Resend
                      </Button>
                    </div>
                  </div>
                  {inviteLinks[inv.id] && (
                    <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`portal-invite-link-block-${i}`}>
                      <p className="text-[11px] text-slate-500 mb-1.5">
                        Share this link manually (valid 30 days) if the email doesn&apos;t arrive:
                      </p>
                      <div className="flex items-center gap-2">
                        <code
                          className="flex-1 min-w-0 truncate text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1.5 font-mono text-slate-600"
                          data-testid={`portal-invite-link-${i}`}
                        >
                          {inviteLinks[inv.id]}
                        </code>
                        <Button
                          variant="outline" size="sm" onClick={() => copyLink(inv.id)}
                          className="flex-shrink-0"
                          data-testid={`portal-invite-copy-inline-${i}`}
                        >
                          {copiedId === inv.id
                            ? <><Check className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Copied</>
                            : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy link</>}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PortalUsersDialog;
